<template>
  <svg
    :viewBox="`0 0 ${width} ${height}`"
    width="100%"
    style="display: block"
    class="caption"
  >
    <defs>
      <clipPath id="sentiment-year-chart-clip">
        <rect
          :x="margin.left - 1"
          y="0"
          :width="width - margin.left - margin.right + 2"
          :height="height + margin.bottom"
        ></rect>
      </clipPath>
    </defs>
    <g :transform="`translate(${margin.left},0)`">
      <g
        v-for="t in y.ticks(height / 40)"
        :key="t"
        :transform="`translate(0,${y(t)})`"
      >
        <text fill="currentColor" x="-9" dy="0.32em" text-anchor="end">
          {{ formatYTick(t) }}
        </text>
        <line
          stroke="currentColor"
          stroke-opacity="0.3"
          :x1="width - margin.left - margin.right"
        ></line>
      </g>
    </g>
    <g clip-path="url(#sentiment-year-chart-clip)">
      <g :transform="`translate(0,${height - margin.bottom})`">
        <g
          v-for="(t, i) in allYears"
          :key="t"
          :transform="`translate(${x(t)}, 0)`"
          :opacity="getYearOpacity(t)"
          style="cursor: pointer"
          @click="$emit('toggle-year', t)"
        >
          <text fill="currentColor" y="9" dy="0.71em" text-anchor="middle">
            {{ formatYearTick(t, i, allYears, true) }}
          </text>
          <line stroke="currentColor" y2="6"></line>
        </g>
      </g>
      <g
        v-for="d in dataByYear"
        :key="d.year"
        :transform="`translate(${x(d.year)},0)`"
      >
        <rect
          v-for="s in d.poemsBySentiment"
          :key="s.sentiment"
          :x="barX(s)"
          :y="barY(s)"
          :width="barWidth"
          :height="barHeight(s)"
          :fill="color(s.sentiment)"
          :fill-opacity="getOpacity({ year: d.year, sentiment: s.sentiment })"
          style="cursor: pointer"
          @click="
            $emit('toggle-block', { year: d.year, sentiment: s.sentiment })
          "
          @mouseenter="blockEntered(s, $event)"
          @mouseleave="blockLeft"
        ></rect>
      </g>
      <g :transform="`translate(0,${height - periodBarHeight - 4})`">
        <g v-for="(p, i) in periods" :key="p.start">
          <rect
            :x="x(p.start - 0.5)"
            :width="x(p.end + 0.5) - x(p.start - 0.5)"
            :height="periodBarHeight"
            fill="none"
            stroke="#000"
          ></rect>
          <text
            :x="getFocusPeriodTextX(p, i)"
            :y="periodBarHeight / 2"
            dy="0.32em"
            text-anchor="middle"
            ref="focusPeriodText"
          >
            {{ p.location }}
          </text>
        </g>
      </g>
    </g>
  </svg>
</template>

<script>
import { scaleLinear, scaleBand } from "d3-scale";
import { max } from "d3-array";

export default {
  props: {
    width: {
      type: Number,
      required: true
    },
    periodBarHeight: {
      type: Number,
      required: true
    },
    brushedYearRange: {
      type: Array,
      required: true
    },
    dataByYear: {
      type: Array,
      required: true
    },
    periods: {
      type: Array,
      required: true
    },
    allYears: {
      type: Array,
      required: true
    },
    color: {
      type: Function,
      required: true
    },
    getOpacity: {
      type: Function,
      required: true
    },
    getYearOpacity: {
      type: Function,
      required: true
    },
    formatYearTick: {
      type: Function,
      required: true
    },
    yValueMode: {
      type: String,
      required: true
    },
    formatYTick: {
      type: Function,
      required: true
    },
    sentiments: {
      type: Array,
      required: true
    },
    barArrangementMode: {
      type: String,
      required: true
    }
  },
  data: () => ({
    height: 160,
    margin: { top: 20, right: 20, bottom: 48, left: 40 },
    labelWidths: []
  }),
  computed: {
    x() {
      return scaleLinear()
        .domain([
          this.brushedYearRange[0] - 0.5,
          this.brushedYearRange[1] + 0.5
        ])
        .range([this.margin.left, this.width - this.margin.right]);
    },
    xInner() {
      return scaleBand()
        .domain(this.sentiments)
        .range([0, this.rectWidth])
        .padding(0.1);
    },
    maxY() {
      let maxY;
      if (this.barArrangementMode === "stacked") {
        maxY = max(this.dataByYear.map(d => d.total[this.yValueMode]));
      } else if (this.barArrangementMode === "grouped") {
        maxY = max(this.dataByYear, d =>
          max(d.poemsBySentiment, v => v.value[this.yValueMode])
        );
      }
      return maxY;
    },
    y() {
      return scaleLinear()
        .domain([0, this.maxY])
        .range([this.height - this.margin.bottom, this.margin.top]);
    },
    rectWidth() {
      return [this.x(1) - this.x(0)] * 0.8;
    },
    barWidth() {
      let width;
      if (this.barArrangementMode === "stacked") {
        width = this.rectWidth;
      } else if (this.barArrangementMode === "grouped") {
        width = this.xInner.bandwidth();
      }
      return width;
    }
  },
  mounted() {
    // Calculate focus period label widths
    this.$refs.focusPeriodText.forEach((text, i) => {
      this.labelWidths[i] = text.getBBox().width;
    });
  },
  methods: {
    barX(s) {
      let x;
      if (this.barArrangementMode === "stacked") {
        x = -this.rectWidth / 2;
      } else if (this.barArrangementMode === "grouped") {
        x = -this.rectWidth / 2 + this.xInner(s.sentiment);
      }
      return x;
    },
    barY(s) {
      let y;
      if (this.barArrangementMode === "stacked") {
        y = this.y(s.stacked[this.yValueMode][1]);
      } else if (this.barArrangementMode === "grouped") {
        y = this.y(s.value[this.yValueMode]);
      }
      return y;
    },
    barHeight(s) {
      let height;
      if (this.barArrangementMode === "stacked") {
        height =
          this.y(s.stacked[this.yValueMode][0]) -
          this.y(s.stacked[this.yValueMode][1]);
      } else if (this.barArrangementMode === "grouped") {
        height = this.y(0) - this.y(s.value[this.yValueMode]);
      }
      return height;
    },
    getFocusPeriodTextX(p, i) {
      let center = (this.x(p.end) + this.x(p.start)) / 2;
      const [xMin, xMax] = this.x.range();
      if (
        this.x(p.start) < xMin &&
        this.x(p.end) > xMin &&
        this.x(p.end) < xMax
      ) {
        center =
          Math.min(
            this.x(p.end + 0.5) - this.labelWidths[i] / 2,
            (xMin + this.x(p.end + 0.5)) / 2
          ) - 3;
      } else if (
        this.x(p.start) < xMax &&
        this.x(p.end) > xMax &&
        this.x(p.start) > xMin
      ) {
        center =
          Math.max(
            this.x(p.start - 0.5) + this.labelWidths[i] / 2,
            (this.x(p.start - 0.5) + xMax) / 2
          ) + 3;
      } else if (this.x(p.start) < xMin && this.x(p.end) > xMax) {
        center = (xMin + xMax) / 2;
      }
      return center;
    },
    blockEntered(d, event) {
      this.$emit("enter-block", d, event);
    },
    blockLeft() {
      this.$emit("leave-block");
    }
  }
};
</script>
